import { Percent } from '@uniswap/sdk-core'
import close from 'assets/svg/close.svg'
import clsx from 'clsx'
import { ButtonPrimaryV2 } from 'components/Button'
import { ModalCenter } from 'components/Modal/ModalCenter'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { SlippageTolerance } from 'state/user/types'
import { useFormatter } from 'utils/formatNumbers'

const data = [
  { title: 'auto', value: '' },
  { title: '0.5%', value: '0.5' },
  { title: '1%', value: '1' },
  { title: '3%', value: '3' },
]

const MINIMUM_RECOMMENDED_SLIPPAGE = new Percent(5, 10_000)
const MAXIMUM_RECOMMENDED_SLIPPAGE = new Percent(1, 100)

function useFormatPercentInput() {
  const { formatPercent } = useFormatter()

  return (slippage: Percent) => formatPercent(slippage).slice(0, -1) // remove % sign
}

const NUMBER_WITH_MAX_TWO_DECIMAL_PLACES = /^(?:\d*\.\d{0,2}|\d+)$/

export function ModalSlippage({
  isVisible,
  setIsVisible,
}: {
  isVisible: boolean
  setIsVisible: Dispatch<SetStateAction<boolean>>
}) {
  const [userSlippageTolerance, setUserSlippageTolerance] = useUserSlippageTolerance()
  const formatPercentInput = useFormatPercentInput()
  const { formatPercent } = useFormatter()

  const defaultSlippageInputValue =
    userSlippageTolerance !== SlippageTolerance.Auto ? formatPercentInput(userSlippageTolerance) : ''

  const [activeTab, setActiveTab] = useState(() => {
    const condition = data.findIndex((item) => item.value === defaultSlippageInputValue)

    return condition
  })
  const [slippageInput, setSlippageInput] = useState(() => {
    if (defaultSlippageInputValue === '') {
      return '0.5'
    }

    return defaultSlippageInputValue
  })

  const warning = useMemo(() => {
    try {
      const parsed = Math.floor(Number.parseFloat(slippageInput) * 100)

      const tooLow = new Percent(parsed, 10_000).lessThan(MINIMUM_RECOMMENDED_SLIPPAGE)
      const tooHigh = new Percent(parsed, 10_000).greaterThan(MAXIMUM_RECOMMENDED_SLIPPAGE)

      if (tooLow) {
        return {
          show: true,
          title: `In case of transactions with ultra-low slippage tolerance, there is a possibility of reversal due to minute market fluctuations`,
        }
      }

      if (tooHigh) {
        return {
          show: true,
          title: `You may receive ${formatPercent(
            new Percent(parsed, 10_000)
          )} less with this percentage of slippage tolerance`,
        }
      }

      return {
        show: false,
      }
    } catch (error) {
      return {
        show: false,
      }
    }
  }, [formatPercent, slippageInput])

  const toggleSlippage = useCallback(
    (item: { title: string; value: string }, idx: number) => {
      return () => {
        if (idx === 0) {
          setActiveTab(idx)
          return setSlippageInput('0.5')
        }

        setActiveTab(idx)
        setSlippageInput(item.value)
      }
    },
    [setActiveTab]
  )

  const parseSlippageInput = (value: string) => {
    if (activeTab === 0) {
      setActiveTab(-1)
    }

    // Do not allow non-numerical characters in the input field or more than two decimals
    if (value.length > 0 && !NUMBER_WITH_MAX_TWO_DECIMAL_PLACES.test(value)) {
      return
    }

    setSlippageInput(value)
    // setSlippageError(false)
  }

  const saveSlippage = useCallback(() => {
    if (activeTab === 0) {
      setUserSlippageTolerance(SlippageTolerance.Auto)

      return setIsVisible((v) => !v)
    }

    if (slippageInput === '.') {
      return
    }

    // Parse user input and set the slippage if valid, error otherwise
    try {
      const parsed = Math.floor(Number.parseFloat(slippageInput) * 100)
      if (parsed > 5000) {
        // setSlippageError(SlippageError.InvalidInput);
      } else {
        setUserSlippageTolerance(new Percent(parsed, 10_000))
      }
    } catch (e) {
      // setSlippageError(SlippageError.InvalidInput);
    } finally {
      setIsVisible((v) => !v)
    }
  }, [activeTab, slippageInput, setUserSlippageTolerance, setIsVisible])

  // Disable scroll when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isVisible])

  return (
    <ModalCenter
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      className="bg-white border border-light3 !p-4 !max-w-[356px]"
    >
      <div>
        <div className="flex flex-1 items-center justify-between mb-[24px]">
          <h4 className="text-[20px] leading-[28px] tracking-[-0.4px] text-light12">Slippage Settings</h4>

          <button onClick={() => setIsVisible((v) => !v)} className="w-6 h-6 flex items-center justify-center">
            <img src={close} alt="close" />
          </button>
        </div>

        <div className="flex items-center gap-[8px] mb-[16px]">
          {data.map((item, idx) => {
            return (
              <button
                onClick={toggleSlippage(item, idx)}
                key={idx}
                className={clsx(
                  'min-w-[75px] rounded-full text-sm capitalize tracking-[-0.32px] border-[2px] h-10 transition-all font-medium',
                  {
                    'border-[#F4F5F6] bg-[#F4F5F6] text-[#777E90]': activeTab !== idx,
                    'border-[#18BBD0] bg-[#F1FAFC] text-[#008095]': activeTab === idx,
                  }
                )}
              >
                {item.title}
              </button>
            )
          })}
        </div>

        <div className="relative flex items-center mb-3">
          <label
            htmlFor="custom"
            className="absolute cursor-pointer left-[24px] text-[16px] leading-[24px] tracking-[-0.32px]"
          >
            Custom
          </label>

          <input
            value={slippageInput}
            id="custom"
            placeholder="0,00%"
            onChange={(e) => {
              parseSlippageInput(e.target.value)
            }}
            className="w-full px-[24px] py-[12px] pl-[80px] text-end rounded-full bg-[#F4F5F6] outline-none text-black placeholder-[#777E90] text-[#003E46] text-[14px] leading-[20px] tracking-[-0.32px]"
          />
        </div>

        {warning.show && <p className="mb-[12px] text-[#FF044B]">{warning.title}</p>}

        <ButtonPrimaryV2 onClick={saveSlippage} className="px-2 py-3 shadow-[0px_4px_0px_0px_#008095]">
          <span>Save settings</span>
        </ButtonPrimaryV2>
      </div>
    </ModalCenter>
  )
}
